<template>
    <div ref="certificateContainer" class="certificate-container">
        <!-- Certificate image rendered from server -->
        <div v-if="src" class="certificate-image">
            <img :src="src" alt="Certificate" class="certificate-img" />
        </div>
        <div v-else class="loading-certificate">
            Loading certificate...
        </div>
    </div>

    <div class="action-buttons">
        <button @click="printAsImage" class="btn btn-primary">
            Print Certificate
        </button>
        <button @click="downloadAsImage" class="btn btn-success">
            Download Certificate
        </button>
        <button v-if="orNo && dateInput && amount && canSaveToIssue" class="btn btn-warning" @click="saveToIssue">
            SAVE TO ISSUE
        </button>
    </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import certificateService from '@/services/certificateService';
import confirmationService from '@/services/confirmationService';
import toastService from '@/services/toastService';
import spinnerService from '@/services/spinnerService';
import Formatters from '@/utils/formatters';
import brgyService from '@/services/brgyService';
/* eslint-disable */
const props = defineProps({
    currentApplication: {
        type: Object,
        default: () => ({})
    },
    currentSoa: {
        type: Object,
        default: () => ({})
    },
    userSignatures: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['print', 'save', 'save-success']);

// Reactive data
const orNo = ref('');
const dateInput = ref('');
const amount = ref('');
const currentCertCode = ref("");
const src = ref(null);
const certificateContainer = ref(null);

const canSaveToIssue = computed(() => {
    if (!props.currentApplication.application?.application_id) return false;

    if (props.currentApplication.issuedCerts?.findIndex(x => x.type === currentCertCode.value.toUpperCase()) === -1)
        return true;

    return false;
});

const saveToIssue = async () => {
    const confirmed = await confirmationService.show("Are you sure you want to save and issue?");
    if (!confirmed) return;
    const payload = {
        ref_date: dateInput.value,
        ref_no: orNo.value,
        type: 'BRC',
        amount: parseFloat(amount.value) || 0,
        period: props.currentApplication?.application?.year,
        application_id: props.currentApplication?.application?.application_id
    };
    spinnerService.show();
    const resp = await certificateService.saveToIssue(payload);
    if (resp.success) {
        toastService.success("Certificate issued successfully!");
        emit("save-success");
    }
    else {
        toastService.error(resp.message);
    }
    spinnerService.hide();
};

const printAsImage = async () => {
    if (!certificateContainer.value) return;

    try {
        // Show loading
        const loading = showLoading('Generating certificate image...');
        // Print the image
        await printImageData(src.value);

        loading.hide();
    } catch (error) {
        console.error('Error generating certificate image:', error);
        alert('Failed to generate certificate. Please try again.');
    }
};

const downloadAsImage = async () => {
    try {
        // Check if we have an image source
        if (!src.value) {
            toastService.error('No certificate image available to download');
            return;
        }

        // Ensure image is loaded
        //await ensureImageLoaded();

        // Create download link
        const link = document.createElement('a');
        const timestamp = new Date().getTime();
        const filename = `brg-certificate-${timestamp}.png`;
        
        // Use the base64 image directly from src.value
        link.download = filename;
        link.href = src.value; // Directly use the base64 string
        link.click();
        
        // Clean up the created element
        setTimeout(() => {
            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
        }, 1000);
        
        toastService.success(`Certificate downloaded as ${filename}`);
    } catch (error) {
        console.error('Error downloading certificate:', error);
        toastService.error('Failed to download certificate: ' + error.message);
    }
};
const printImageData = (imageData) => {
    return new Promise((resolve, reject) => {
        // Create HTML content for printing
        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Print Certificate</title>
                <style>
                    @page {
                        margin: 0;
                        size: A4 portrait;
                    }
                    body {
                        margin: 0;
                        padding: 0;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        min-height: 100vh;
                        background: white;
                    }
                    .print-image {
                        width: 100%;
                        max-width: 210mm; /* A4 width */
                        height: auto;
                        display: block;
                    }
                </style>
            </head>
            <body>
                <img src="${imageData}" class="print-image" />
                <script>
                    // Auto print when loaded
                    window.onload = function() {
                        setTimeout(function() {
                            window.print();
                        }, 300);
                    };
                    
                    // Close tab after printing
                    window.onafterprint = function() {
                        setTimeout(function() {
                            window.close();
                        }, 500);
                    };
                    
                    // Fallback: close after 30 seconds
                    setTimeout(function() {
                        if (!window.closed) {
                            window.close();
                        }
                    }, 30000);
                <\/script>
            </body>
            </html>
        `;

        // Create a new tab
        const printTab = window.open('', '_blank');
        
        if (!printTab) {
            reject(new Error('Popup blocked. Please allow popups for this site.'));
            return;
        }

        // Write content to the tab
        printTab.document.open();
        printTab.document.write(htmlContent);
        printTab.document.close();

        // Focus the new tab
        printTab.focus();

        // Resolve promise when tab is closed or after reasonable timeout
        const checkClosed = setInterval(() => {
            if (printTab.closed) {
                clearInterval(checkClosed);
                resolve();
            }
        }, 1000);

        // Timeout after 30 seconds
        setTimeout(() => {
            clearInterval(checkClosed);
            if (!printTab.closed) {
                printTab.close();
            }
            resolve();
        }, 30000);
    });
};

const showLoading = (message) => {
    const loading = document.createElement('div');
    loading.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0,0,0,0.7);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;
        color: white;
        font-size: 18px;
        flex-direction: column;
    `;
    loading.innerHTML = `
        <div class="spinner-border text-light" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
        <div style="margin-top: 20px">${message}</div>
    `;
    
    document.body.appendChild(loading);

    return {
        hide: () => {
            if (loading.parentNode) {
                loading.parentNode.removeChild(loading);
            }
        }
    };
};

const preProcessCertInfo = (certInfo) => {
    if (!certInfo) return;
    orNo.value = certInfo.ref_no;
    dateInput.value = Formatters.formatDateForInput(certInfo.ref_date);
    amount.value = certInfo.amount;
};

const getSignatories = async (code) => {
    currentCertCode.value = code;
    const certInfo = props.currentApplication.issuedCerts?.find(x => x.type === code.toUpperCase());
    preProcessCertInfo(certInfo);
};

const getBrgyClearanceImage = async() => {
    spinnerService.show();
    try {
        const resp = await brgyService.getClearanceImageById(props.currentApplication.application.application_id);
        src.value = resp.img;
    } catch (error) {
        console.error('Error loading certificate image:', error);
        toastService.error('Failed to load certificate image');
    } finally {
        spinnerService.hide();
    }
};

defineExpose({ getSignatories, getBrgyClearanceImage });

// Watch for changes to clear fields
watch(() => props.currentApplication, (newApp) => {
    if (!newApp || !newApp.application) {
        orNo.value = '';
        dateInput.value = '';
        amount.value = '';
        src.value = null;
    }
}, { deep: true });

// Load certificate image when component mounts with current application
onMounted(() => {
    if (props.currentApplication?.application?.application_id) {
        getBrgyClearanceImage();
    }
});
</script>

<style scoped>
/* Certificate container */
.certificate-container {
    background-color: white;
    padding: 20px;
    width: 100%;
    max-width: 1000px;
    margin: 0 auto;
    min-height: auto;
    box-sizing: border-box;
    position: relative;
    font-family: 'Times New Roman', Times, serif;
    overflow: auto;
}

/* Certificate image styling */
.certificate-image {
    width: 100%;
    text-align: center;
}

.certificate-img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 0 auto;
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.loading-certificate {
    padding: 100px;
    text-align: center;
    color: #666;
    font-size: 18px;
}

/* Action buttons */
.action-buttons {
    text-align: center;
    margin-top: 20px;
    margin-bottom: 30px;
    padding: 0 20px;
}

.action-buttons .btn {
    margin: 5px;
    min-width: 180px;
}

/* PRINT STYLES */
@media print {
    @page {
        size: A4 portrait;
        margin: 0;
    }

    body, html {
        margin: 0;
        padding: 0;
        background: white !important;
        width: 210mm;
        min-height: 297mm;
    }

    /* Hide all elements except certificate container */
    body * {
        visibility: hidden;
    }

    .certificate-container,
    .certificate-container * {
        visibility: visible;
    }

    .certificate-container {
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        width: 100% !important;
        max-width: 100% !important;
        height: 100% !important;
        min-height: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        overflow: visible !important;
    }

    .certificate-img {
        width: 100% !important;
        height: 100% !important;
        max-width: 100% !important;
        max-height: 100% !important;
        object-fit: contain;
    }

    /* Hide action buttons when printing */
    .action-buttons {
        display: none !important;
    }
}

/* SCREEN STYLES */
@media screen {
    .certificate-container {
        max-height: 80vh;
        overflow-y: auto;
        border: 1px solid #ddd;
        border-radius: 8px;
    }

    /* Scrollbar styling */
    .certificate-container::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    .certificate-container::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
    }

    .certificate-container::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;
    }

    .certificate-container::-webkit-scrollbar-thumb:hover {
        background: #555;
    }
}

/* Responsive adjustments */
@media screen and (max-width: 1100px) {
    .certificate-container {
        max-width: 95%;
        padding: 15px;
    }
}

@media screen and (max-width: 768px) {
    .certificate-container {
        max-width: 100%;
        padding: 10px;
    }

    .action-buttons {
        padding: 0 10px;
    }

    .action-buttons .btn {
        width: 100%;
        margin: 5px 0;
    }
}

/* Ensure image doesn't exceed container */
.certificate-img {
    max-width: 100%;
    height: auto;
}

/* Loading spinner */
.spinner-border {
    width: 3rem;
    height: 3rem;
}
</style>